import { useState } from 'react';

export interface FilterState {
  year: string;
  region: string;
  sort: string;
}

interface FilterBarProps {
  years: number[];
  regions: string[];
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

const SORTS = [
  { key: 'default', label: '默认排序' },
  { key: 'latest', label: '最新' },
  { key: 'hot', label: '最热' },
];

function FilterGroup({
  label,
  options,
  active,
  onSelect,
}: {
  label: string;
  options: { key: string; label: string }[];
  active: string;
  onSelect: (key: string) => void;
}) {
  return (
    <div className="flex items-start gap-3 py-1">
      <span className="shrink-0 w-12 pt-1 text-sm text-foreground-500">{label}</span>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => onSelect(opt.key)}
            className={`px-3 py-1.5 rounded-md text-sm whitespace-nowrap transition-colors cursor-pointer ${
              active === opt.key
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-700 hover:bg-background-200'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function FilterBar({ years, regions, value, onChange, onReset }: FilterBarProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  const yearOptions = [{ key: 'all', label: '全部' }, ...years.map((y) => ({ key: String(y), label: `${y}年` }))];
  const regionOptions = [{ key: 'all', label: '全部' }, ...regions.map((r) => ({ key: r, label: r }))];
  const sortOptions = SORTS.map((s) => ({ key: s.key, label: s.label }));

  const update = (patch: Partial<FilterState>) => onChange({ ...value, ...patch });

  const body = (
    <div className="space-y-2">
      <FilterGroup
        label="年份"
        options={yearOptions}
        active={value.year}
        onSelect={(k) => {
          update({ year: k });
          setDrawerOpen(false);
        }}
      />
      <FilterGroup
        label="地区"
        options={regionOptions}
        active={value.region}
        onSelect={(k) => {
          update({ region: k });
          setDrawerOpen(false);
        }}
      />
      <FilterGroup
        label="排序"
        options={sortOptions}
        active={value.sort}
        onSelect={(k) => {
          update({ sort: k });
          setDrawerOpen(false);
        }}
      />
      <div className="pt-2 border-t border-background-200 mt-3">
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-700 transition-colors cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          重置筛选
        </button>
      </div>
    </div>
  );

  return (
    <div className="rounded-lg bg-background-50 border border-background-200 p-4 md:p-5">
      <div className="hidden md:block">{body}</div>

      <div className="md:hidden">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer"
          >
            <i className="ri-filter-3-line"></i>
            筛选条件
          </button>
          <button
            type="button"
            onClick={onReset}
            className="text-sm text-foreground-500 whitespace-nowrap cursor-pointer"
          >
            重置
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-foreground-950/50"
            onClick={() => setDrawerOpen(false)}
          ></div>
          <div className="absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-xl p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-foreground-950">筛选条件</h3>
              <button
                type="button"
                aria-label="关闭"
                onClick={() => setDrawerOpen(false)}
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            {body}
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium cursor-pointer"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </div>
  );
}